// account-actions.js — Sign-out + Delete-account handlers for the
// Account page. Wires entirely at module init.
//
// Three flows live here:
//
//   #account-signout      — sign out the current session only
//   #account-signout-all  — sign out every device (Supabase global
//                            scope; daemon on every approved
//                            machine has to re-link)
//   #account-delete       — enterprise delete-account flow:
//                            1. sudo-mode reauth
//                            2. POST /v1/user/delete-request
//                            3. on 409 → show showDeleteBlockersModal
//                               (sole-admin or active subs)
//                            4. on 200 → "check your email"
//
// showDeleteBlockersModal renders the rich error modal returned by
// the coordinator when delete is blocked by sole-admin teams or
// active subscriptions. Includes inline promote dropdowns + nav
// shortcuts to Team / Billing.
//
// Dependencies:
//   getSupabase                  — bridge (supabase is closure-bound)
//   signOutAfterPasswordChange   — bridge (mutates auth-state closure)
//   requireReauth, clearReauth   — direct imports from reauth.js
//
// signOutAfterPasswordChange is reused for both sign-out paths since
// it's the same cleanup tail.
//
// Hoisted from bootstrap.js on 2026-05-13 (Phase K Tier 2f); bridge
// calls switched to direct imports for reauth helpers in Tier 2n.
import { $ } from "./dom.js";
import { escapeHTML } from "./util.js";
import { showToast, showConfirm, closeAllConfirms } from "./ui.js";
import { navigate } from "./router.js";
import { requireReauth, clearReauth } from "./reauth.js";

const COORDINATOR =
  (typeof window !== "undefined" &&
    window.AC_CONFIG &&
    window.AC_CONFIG.API_BASE) ||
  "";

function getSupabase() {
  const legacy = window.AC_LEGACY;
  return legacy && typeof legacy.getSupabase === "function"
    ? legacy.getSupabase()
    : null;
}

function bridgeCall(name, ...args) {
  const legacy = window.AC_LEGACY;
  const fn = legacy && legacy[name];
  if (typeof fn === "function") return fn(...args);
  return undefined;
}

// signOutLocal: the common cleanup tail used by both Sign Out and
// Sign Out Everywhere. signOutAfterPasswordChange in bootstrap.js
// does the same thing (booted=false; state.token=null;
// clearAuthHintCookie; showAuth) plus an internal supabase.signOut.
// We don't reuse signOutAfterPasswordChange directly because both
// Sign Out paths here have already called supabase.signOut with
// the scope they want and would double-sign-out if they called it
// again.
async function signOutLocal() {
  await bridgeCall("signOutAfterPasswordChange");
}

// ---------- Sign out (current session) ----------

async function handleSignOut() {
  const ok = await showConfirm(
    "Sign out?",
    "You will need to sign in again to access the dashboard.",
    "Sign Out",
  );
  if (!ok) return;
  const supabase = getSupabase();
  if (supabase) {
    try {
      await supabase.auth.signOut();
    } catch (e) {
      showToast("error", "Sign out failed: " + (e.message || "unknown"));
      return;
    }
  }
  await signOutLocal();
}

// ---------- Sign out everywhere ----------

async function handleSignOutAll() {
  const ok = await showConfirm(
    "Sign out of every device?",
    "All of your active sessions will end and every device signed in as you will need to sign in again. This includes the daemon on any machine you’ve approved.",
    "Sign Out Everywhere",
  );
  if (!ok) return;
  try {
    const supabase = getSupabase();
    if (supabase) await supabase.auth.signOut({ scope: "global" });
  } catch (e) {
    showToast("error", "Sign out failed: " + (e.message || "unknown"));
    return;
  }
  await signOutLocal();
}

// ---------- Delete account ----------

async function handleDeleteAccount(btn) {
  const prev = btn.textContent;
  btn.disabled = true;
  btn.textContent = "Preparing…";

  let reauthToken;
  try {
    reauthToken = await requireReauth();
  } catch (e) {
    btn.disabled = false;
    btn.textContent = prev;
    if (e && e.message === "reauth_cancelled") return;
    showToast(
      "error",
      "Could not verify password: " + (e.message || "unknown"),
    );
    return;
  }

  try {
    const supabase = getSupabase();
    if (!supabase) throw new Error("Not signed in.");
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) throw new Error("Not signed in.");
    const r = await fetch(COORDINATOR + "/v1/user/delete-request", {
      method: "POST",
      headers: {
        Authorization: "Bearer " + session.access_token,
        "X-Reauth-Token": reauthToken,
        "Content-Type": "application/json",
      },
      body: "{}",
    });

    if (r.status === 409) {
      const data = await r.json().catch(() => ({}));
      await showDeleteBlockersModal(data);
      return;
    }
    if (!r.ok) {
      const e = await r.json().catch(() => ({}));
      if (r.status === 403 || e.error === "reauth_required") {
        clearReauth();
      }
      throw new Error(e.message || e.error || "Could not request deletion.");
    }
    const data = await r.json();
    // Success path — email sent.
    const pendingCancels = data.pending_cancel_subscriptions || [];
    let body =
      "Check your email. We sent a confirmation link to your inbox. Click it within 24 hours to permanently delete your account.";
    if (pendingCancels.length > 0) {
      body +=
        "<br><br><strong>Heads up:</strong> " +
        pendingCancels.length +
        " subscription" +
        (pendingCancels.length === 1 ? " was" : "s were") +
        " scheduled to cancel at period end. Confirming deletion will cancel " +
        (pendingCancels.length === 1 ? "it" : "them") +
        " immediately (no further charges).";
    }
    await showConfirm("Confirmation email sent", body, "Got it");
  } catch (e) {
    showToast("error", "Failed to request deletion: " + (e.message || "unknown"));
  } finally {
    btn.disabled = false;
    btn.textContent = prev;
  }
}

// Renders the blockers modal returned by /v1/user/delete-request (409).
async function showDeleteBlockersModal(data) {
  const supabase = getSupabase();
  const soleAdmin = data.sole_admin_teams || [];
  const activeSubs = data.active_subscriptions || [];
  const pendingCancels = data.pending_cancel_subscriptions || [];

  // Pre-fetch members for each sole-admin team so we can render an
  // inline promote dropdown. X-Team-ID scopes the query when the
  // caller belongs to multiple teams.
  const memberLists = await Promise.all(
    soleAdmin.map(async (t) => {
      try {
        if (!supabase) return { team_id: t.team_id, members: [] };
        const sess = await supabase.auth.getSession();
        const tok =
          sess.data && sess.data.session && sess.data.session.access_token;
        if (!tok) return { team_id: t.team_id, members: [] };
        const r = await fetch(COORDINATOR + "/v1/teams/members", {
          headers: {
            Authorization: "Bearer " + tok,
            "X-Team-ID": t.team_id,
          },
        });
        if (!r.ok) return { team_id: t.team_id, members: [] };
        const body = await r.json();
        const candidates = (body.people || []).filter(
          (p) => p.user_id && p.role !== "admin",
        );
        return { team_id: t.team_id, members: candidates };
      } catch {
        return { team_id: t.team_id, members: [] };
      }
    }),
  );
  const membersByTeam = Object.fromEntries(
    memberLists.map((m) => [m.team_id, m.members]),
  );

  let html =
    '<p style="font-size: 14px; color: var(--text-secondary); line-height: 1.6; margin: 0 0 16px;">Resolve these before you can delete your account:</p>';
  if (soleAdmin.length > 0) {
    html +=
      '<div style="background: var(--bg-panel-solid); border: 1px solid var(--border); border-radius: var(--r-md); padding: 12px; margin-bottom: 12px;">' +
      '<div style="font-weight: 600; font-size: 14px; color: var(--danger); margin-bottom: 6px;">You’re the only admin of ' +
      soleAdmin.length +
      " team" +
      (soleAdmin.length === 1 ? "" : "s") +
      "</div>" +
      '<div style="font-size: 13px; color: var(--text-secondary); line-height: 1.6; margin-bottom: 8px;">Promote a teammate to admin so the team isn’t orphaned when you leave.</div>';
    for (const t of soleAdmin) {
      const candidates = membersByTeam[t.team_id] || [];
      html +=
        '<div style="margin-top: 10px; font-size: 13px; color: var(--text);"><strong>' +
        escapeHTML(t.team_name) +
        '</strong> <span style="color: var(--text-tertiary); font-size: 12px;">(' +
        (t.member_count || 0) +
        " member" +
        (t.member_count === 1 ? "" : "s") +
        ")</span></div>";
      if (candidates.length === 0) {
        html +=
          '<div style="font-size: 12px; color: var(--text-tertiary); margin: 4px 0 8px 0;">No other members to promote. Invite someone first, or delete the team instead.</div>';
      } else {
        const optionsHtml = candidates
          .map(
            (c) =>
              '<option value="' +
              escapeHTML(c.user_id) +
              '">' +
              escapeHTML(c.email) +
              "</option>",
          )
          .join("");
        html +=
          '<div style="display: flex; gap: 8px; margin: 6px 0 4px 0; align-items: center; flex-wrap: wrap;">' +
          '<select class="prompt-input" data-transfer-team-id="' +
          escapeHTML(t.team_id) +
          '" style="flex: 1; min-width: 180px; padding: 6px 8px; font-size: 12px;">' +
          optionsHtml +
          "</select>" +
          '<button class="btn btn-sm" data-transfer-action="' +
          escapeHTML(t.team_id) +
          '" data-team-name="' +
          escapeHTML(t.team_name) +
          '">Promote &amp; continue</button>' +
          "</div>";
      }
    }
    html += "</div>";
  }
  if (activeSubs.length > 0) {
    html +=
      '<div style="background: var(--bg-panel-solid); border: 1px solid var(--border); border-radius: var(--r-md); padding: 12px; margin-bottom: 12px;">' +
      '<div style="font-weight: 600; font-size: 14px; color: var(--danger); margin-bottom: 6px;">You have ' +
      activeSubs.length +
      " active paid subscription" +
      (activeSubs.length === 1 ? "" : "s") +
      "</div>" +
      '<div style="font-size: 13px; color: var(--text-secondary); line-height: 1.6; margin-bottom: 8px;">Cancel before deleting so you don’t keep getting charged.</div>' +
      '<ul style="margin: 8px 0 0 0; padding-left: 18px; font-size: 13px; color: var(--text);">' +
      activeSubs
        .map(
          (s) =>
            '<li style="margin-bottom: 4px;">' +
            escapeHTML(s.team_name) +
            ' <span style="color: var(--text-tertiary); font-size: 12px;">(' +
            escapeHTML(s.tier || "") +
            " · " +
            escapeHTML(s.status || "") +
            ")</span></li>",
        )
        .join("") +
      "</ul>" +
      '<button class="btn btn-sm" id="delete-blocker-billing-btn" style="margin-top: 10px;">Go to Billing</button>' +
      "</div>";
  }
  if (pendingCancels.length > 0) {
    html +=
      '<div style="background: var(--bg-elevated); border: 1px solid var(--border); border-radius: var(--r-md); padding: 12px; margin-bottom: 12px; font-size: 13px; color: var(--text-secondary); line-height: 1.6;">' +
      "<strong>Info:</strong> " +
      pendingCancels.length +
      " subscription" +
      (pendingCancels.length === 1 ? "" : "s") +
      " already scheduled to cancel at period end. When you confirm deletion, " +
      (pendingCancels.length === 1 ? "it" : "they") +
      " will cancel immediately." +
      "</div>";
  }
  await showConfirm(
    data.message || "Cannot delete account yet",
    html,
    "Close",
    { hideCancel: true },
  );
  // Wire action buttons if they're present in the DOM after showConfirm rendered.
  const teamBtn = document.getElementById("delete-blocker-team-btn");
  if (teamBtn)
    teamBtn.addEventListener("click", () => {
      closeAllConfirms();
      navigate("team");
    });
  const billBtn = document.getElementById("delete-blocker-billing-btn");
  if (billBtn)
    billBtn.addEventListener("click", () => {
      closeAllConfirms();
      navigate("settings");
    });

  // Wire up the per-team Promote & continue buttons. Each one promotes
  // the selected member to admin in that team, then re-runs the delete-
  // request so the modal updates.
  document.querySelectorAll("[data-transfer-action]").forEach((btn) => {
    btn.addEventListener("click", async () => {
      const teamId = btn.dataset.transferAction;
      const teamName = btn.dataset.teamName || teamId;
      const select = document.querySelector(
        '[data-transfer-team-id="' + CSS.escape(teamId) + '"]',
      );
      if (!select || !select.value) return;
      const userId = select.value;
      const userEmail = select.options[select.selectedIndex]
        ? select.options[select.selectedIndex].text
        : userId;

      const confirmed = await showConfirm(
        "Promote " + userEmail + " to admin?",
        "They will become an admin of <strong>" +
          escapeHTML(teamName) +
          "</strong>. Then we will retry the account deletion.",
        "Promote",
      );
      if (!confirmed) return;

      const prev = btn.textContent;
      btn.disabled = true;
      btn.textContent = "Promoting…";
      try {
        const supabase = getSupabase();
        if (!supabase) throw new Error("Not signed in.");
        const sess = await supabase.auth.getSession();
        const tok =
          sess.data && sess.data.session && sess.data.session.access_token;
        if (!tok) throw new Error("Not signed in.");
        const r = await fetch(
          COORDINATOR +
            "/v1/teams/members/by-user/" +
            encodeURIComponent(userId),
          {
            method: "PATCH",
            headers: {
              Authorization: "Bearer " + tok,
              "Content-Type": "application/json",
              "X-Team-ID": teamId,
            },
            body: JSON.stringify({ role: "admin" }),
          },
        );
        if (!r.ok) {
          const e = await r.json().catch(() => ({}));
          throw new Error(e.message || e.error || "Promotion failed");
        }
        showToast(
          "success",
          "Promoted " + userEmail + " to admin of " + teamName + ".",
        );
        closeAllConfirms();
        // Re-trigger the delete flow now that this blocker is resolved.
        const deleteBtn = $("account-delete");
        if (deleteBtn) deleteBtn.click();
      } catch (err) {
        showToast("error", "Could not promote: " + (err.message || "unknown"));
        btn.disabled = false;
        btn.textContent = prev;
      }
    });
  });

  // Allow inline links inside the modal body to navigate.
  document.querySelectorAll("[data-nav-target]").forEach((a) => {
    a.addEventListener("click", (e) => {
      e.preventDefault();
      closeAllConfirms();
      navigate(a.dataset.navTarget);
    });
  });
}

// ---- module init ----

function wireListeners() {
  const signoutBtn = $("account-signout");
  if (signoutBtn) signoutBtn.addEventListener("click", handleSignOut);
  const signoutAllBtn = $("account-signout-all");
  if (signoutAllBtn)
    signoutAllBtn.addEventListener("click", handleSignOutAll);
  const deleteBtn = $("account-delete");
  if (deleteBtn) {
    deleteBtn.addEventListener("click", () => handleDeleteAccount(deleteBtn));
  }
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", wireListeners);
} else {
  wireListeners();
}
